import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardBody, Link, Image, Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";
import { FileTypeIcon } from "@/components/UI/FileTypeIcon";

interface SourceFile {
  id: string;
  title: string;
  summary: string;
  icon: string;
  timestamp: string;
  uploaded_by: string;
}

interface SourceWeb {
  title: string;
  summary: string;
  favicon: string;
  url: string;
}

interface MessageSourcesProps {
  sourcesFiles: SourceFile[];
  sourcesWeb: SourceWeb[];
}

const MessageSources: React.FC<MessageSourcesProps> = ({ sourcesFiles = [], sourcesWeb = [] }) => {
  if (sourcesFiles.length === 0 && sourcesWeb.length === 0) {
    return <></>;
  }

  const [showAllSources, setShowAllSources] = useState(false);
  const [visibleSources, setVisibleSources] = useState<number>(3);
  const [expandedSummaries, setExpandedSummaries] = useState<{
    [key: number]: boolean;
  }>({});
  const { platformClient } = useWebuildAPIClient();
  const { brand } = useBrand();

  const downloadFile = async (fileId: string) => {
    if (!platformClient) return;

    const response = await platformClient.files.createTemporaryDownloadUrl({
      fileId: fileId,
    });

    if (response && response.temporary_url) {
      window.open(`https://api.${window.location.hostname}${response.temporary_url}`, "_blank");
    }
  };

  const { t } = useTranslation();

  useEffect(() => {
    // Adjust number of visible sources based on screen size
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 640) {
        // xs
        setVisibleSources(1);
      } else if (width < 768) {
        // sm
        setVisibleSources(2);
      } else if (width < 1024) {
        // md
        setVisibleSources(3);
      } else {
        // lg and above
        setVisibleSources(4);
      }
    };

    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSourcesView = () => {
    setShowAllSources(!showAllSources);
  };

  const toggleSummary = (index: number) => {
    setExpandedSummaries((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const hiddenSourcesCount = sourcesFiles.length + sourcesWeb.length - visibleSources;

  const renderSummary = (summary: string, index: number, maxLength: number = 180) => {
    const isExpanded = expandedSummaries[index];
    const shouldShowToggle = summary.length > maxLength;

    if (!shouldShowToggle) {
      return (
        <div className="text-xs">
          <MarkdownWeBuild>{summary}</MarkdownWeBuild>
        </div>
      );
    }

    return (
      <div className="text-xs">
        <MarkdownWeBuild>
          {isExpanded ? summary : `${summary.slice(0, maxLength)}...`}
        </MarkdownWeBuild>
        <Button
          className="p-0 min-w-0 h-auto text-xs"
          size="sm"
          variant="light"
          onPress={() => toggleSummary(index)}
        >
          {isExpanded ? "Show less" : "Show more"}
        </Button>
      </div>
    );
  };

  const visibleWebSources = Math.max(0, visibleSources - sourcesFiles.length);

  return (
    <div className="mt-4">
      {/** Collapsible sources - Files */}
      {!showAllSources ? (
        <div className="flex flex-row gap-4 overflow-x-auto pb-2">
          {sourcesFiles.slice(0, visibleSources).map((source: SourceFile, index: number) => (
            <Card
              key={index}
              className="min-w-[250px] max-w-[300px] flex-shrink-0"
              data-id={`source-card-${index}`}
            >
              <CardHeader className="flex flex-row gap-2">
                <FileTypeIcon height={40} icon={source.icon} style={{ minWidth: "40px" }} />
                <div className="flex flex-col">
                  <span
                    className="text-sm cursor-pointer hover:underline"
                    data-id={`source-title-${index}`}
                    role="button"
                    tabIndex={0}
                    title="Download file"
                    onClick={() => {
                      if ("id" in source && source.id && source.title) {
                        downloadFile(source.id);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if ("id" in source && source.id && source.title) {
                          downloadFile(source.id);
                        }
                      }
                    }}
                  >
                    {source.title}
                  </span>
                  <p className="text-xs text-small-500 truncate">
                    {source.uploaded_by} <br /> {source.timestamp}
                  </p>
                </div>
              </CardHeader>
              <CardBody className="pt-0 gap-top-0">{renderSummary(source.summary, index)}</CardBody>
            </Card>
          ))}

          {sourcesWeb.slice(0, visibleWebSources).map((source: SourceWeb, index: number) => (
            <Card
              key={index}
              className="min-w-[250px] max-w-[300px] flex-shrink-0"
              data-id={`source-card-${index}`}
            >
              <CardHeader className="flex flex-row gap-2">
                <Image
                  alt={`${source.title} icon`}
                  fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%23e5e5e5'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='10' fill='%23999'%3EIcon%3C/text%3E%3C/svg%3E"
                  height={40}
                  radius="sm"
                  src={source.favicon}
                  style={{ minWidth: "40px" }}
                  width={40}
                />
                <div className="flex flex-col">
                  <Link
                    isExternal
                    showAnchorIcon
                    className="text-sm"
                    data-id={`source-title-${index}`}
                    href={source.url || ""}
                  >
                    {source.title}
                  </Link>
                  <p className="text-xs text-small-500 truncate">{source.url || ""}</p>
                </div>
              </CardHeader>
              <CardBody className="pt-0 gap-top-0">{renderSummary(source.summary, index)}</CardBody>
            </Card>
          ))}

          {hiddenSourcesCount > 0 && (
            <Button className="self-center btn-brand-primary" onPress={toggleSourcesView}>
              +{hiddenSourcesCount} sources
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sourcesFiles.map((source: SourceFile, index: number) => (
            <div key={index} className="flex flex-row gap-3 p-3 border-b">
              <FileTypeIcon height={40} icon={source.icon} style={{ minWidth: "40px" }} />
              <div className="flex flex-col w-1/10">
                <Link
                  isExternal
                  showAnchorIcon
                  className="text-sm"
                  data-id={`source-title-${index}`}
                  onPress={() => {
                    if ("id" in source && source.id && source.title) {
                      downloadFile(source.id);
                    }
                  }}
                >
                  {source.title}
                </Link>
                <p className="text-xs text-small-500 truncate">
                  {source.uploaded_by} <br /> {source.timestamp}
                </p>
              </div>
              <div className="flex flex-col flex-grow text-sm">
                {renderSummary(source.summary, index, 2000)}
              </div>
            </div>
          ))}

          {sourcesWeb.map((source: SourceWeb, index: number) => (
            <div key={index} className="flex flex-row gap-3 p-3 border-b">
              <Image
                alt={`${source.title} icon`}
                fallbackSrc={`/brands/${brand.prefix || "webuild"}/pathfinder.svg`}
                height={40}
                radius="sm"
                src={source.favicon}
                style={{ minWidth: "40px" }}
                width={40}
              />
              <div className="flex flex-col w-1/10">
                <Link
                  isExternal
                  showAnchorIcon
                  className="text-sm"
                  data-id={`source-title-${index}`}
                  href={source.url || ""}
                >
                  {source.title}
                </Link>
                <p className="text-xs text-small-500 truncate">{source.url || ""}</p>
              </div>
              <div className="flex flex-col flex-grow text-sm">
                {renderSummary(source.summary, index)}
              </div>
            </div>
          ))}

          <Button
            className="self-start mt-2 btn-brand-primary"
            startContent={<Icon icon="solar:arrow-left-linear" />}
            onPress={toggleSourcesView}
          >
            {t("Show less")}
          </Button>
        </div>
      )}
    </div>
  );
};

export { MessageSources, type SourceFile, type SourceWeb };
